import { Folder } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { getSigners } from 'lib/api/business';
import {
  createPortfolio,
  createPortfolioFromTemplate,
  getPortfoliosTemplates,
  getPortoflioValidations,
} from 'lib/api/integrations/digitalSignature';
import {
  GetTemplatesResponse,
  PortfolioFromTemplate,
} from 'lib/api/integrations/digitalSignature';
import CustomButton from 'lib/designSystem/Button';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { PortfolioValidation, SignatureType, User } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { OperationAssociationValue } from '../OperationAssociation/operations';
import Custom from './Components/Custom';
import { PendingDocument } from './Components/DocumentsDropzone';
import Template from './Components/Template';

interface CreatePortfolioProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessIdentifier: string | undefined;
  fetchPortfolios: ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => Promise<void>;
}
export interface Signer {
  nin: string;
  country: string;
  names: string;
  lastnames: string;
  email: string;
  phone?: string;
  notification: string;
  order: number;
}

const CreatePortfolio = ({
  open,
  setOpen,
  businessIdentifier,
  fetchPortfolios,
}: CreatePortfolioProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<'custom' | 'template'>('custom');
  const [template, setTemplate] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState<string>('');
  const [templates, setTemplates] = useState<GetTemplatesResponse[]>([]);
  const [ownUsers, setOwnUsers] = useState<User[]>([]);
  const [businessUsers, setBusinessUsers] = useState<User[]>([]);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [selectedOwnUserIds, setSelectedOwnUserIds] = useState<number[]>([]);
  const [validations, setValidations] = useState<PortfolioValidation[]>([]);
  const [selectedValidations, setSelectedValidations] = useState<number[]>([]);
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [businessUserIds, setBusinessUserIds] = useState<number[]>([]);
  const [operation, setOperation] = useState<OperationAssociationValue>({
    enabled: false,
  });

  const fetchSigners = async () => {
    try {
      if (!businessIdentifier)
        return showAlert(dispatch, 'error', 'Business identifier is required');
      const ownUsersResponse: User[] = await getSigners(dispatch);
      setOwnUsers(ownUsersResponse);
      setSelectedOwnUserIds(ownUsersResponse.map((u: User) => u.id));
      const businessUsersResponse: User[] = await getSigners(
        dispatch,
        businessIdentifier,
      );
      setBusinessUsers(businessUsersResponse);
      setBusinessUserIds(businessUsersResponse.map((u: User) => u.id));
    } catch (error) {
      console.error('Error fetching signers:', error);
    }
  };

  const fetchPortoflioValidations = async () => {
    try {
      const validations = await getPortoflioValidations(dispatch);
      setValidations(validations);
    } catch (error) {
      console.error('Error fetching validations:', error);
      showAlert(dispatch, 'error', 'Error fetching validations');
    }
  };

  const fetchTemplates = async () => {
    try {
      const templates = await getPortfoliosTemplates(dispatch);
      setTemplates(templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const parseSigner = (user: User) => {
    const name = user.name?.split(' ')[0] ?? '';
    const lastname = user.name?.split(' ')[1] ?? '';
    return {
      nin: user.identifier,
      country: 'CL',
      names: name,
      lastnames: lastname,
      email: user.email,
      phone: user.phone ?? undefined,
      notification: 'email',
      order: 1,
    };
  };

  const parseSigners = () => {
    const selectedOwnList = ownUsers.filter((u) =>
      selectedOwnUserIds.includes(u.id),
    );
    const selectedBusinessList = businessUsers.filter((u) =>
      businessUserIds.includes(u.id),
    );
    return [...selectedOwnList, ...selectedBusinessList].map(parseSigner);
  };

  const handleCreatePortfolio = async () => {
    try {
      setLoading(true);
      if (!businessIdentifier) {
        showAlert(dispatch, 'error', 'Business identifier is required');
        return;
      }

      const fd = new FormData();

      if (type === 'custom' && documents.length > 0) {
        documents.forEach((d) => {
          fd.append('files', d.file);
        });
        const filesInfo = documents.map((d) => ({
          description: d.description,
          signature: d.signature as SignatureType,
        }));
        fd.append('filesInfo', JSON.stringify(filesInfo));
      }

      fd.append('signers', JSON.stringify(parseSigners()));
      fd.append('businessIdentifier', businessIdentifier);
      fd.append('title', title);

      if (validUntil) {
        fd.append('validUntil', validUntil.format('YYYY-MM-DD'));
      }

      if (operation.enabled && operation.operation && operation.operationType) {
        fd.append(
          'request',
          JSON.stringify({
            type: operation.operationType,
            id: operation.operation.id,
          }),
        );
      }

      if (selectedValidations.length > 0) {
        const selectedTypes = selectedValidations
          .map((id) => validations.find((v) => v.id === id)?.type)
          .filter((t): t is string => Boolean(t));
        if (selectedTypes.length > 0) {
          fd.append('validations', JSON.stringify(selectedTypes));
        }
      }
      await createPortfolio(dispatch, fd);
      showAlert(dispatch, 'success', 'Portafolio creado exitosamente');
      setOpen(false);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortfolioFromTemplate = async () => {
    try {
      setLoading(true);
      if (!businessIdentifier || !template) {
        showAlert(dispatch, 'error', 'Business identifier is required');
        return;
      }

      const body: PortfolioFromTemplate = {
        templateId: template,
        businessIdentifier: businessIdentifier,
        signers: parseSigners(),
        title: title,
        ...(validUntil && { validUntil: validUntil.format('YYYY-MM-DD') }),
        ...(operation.enabled &&
          operation.operation &&
          operation.operationType && {
            request: {
              type: operation.operationType,
              id: operation.operation.id,
            },
          }),
      };
      await createPortfolioFromTemplate(dispatch, body);
      showAlert(dispatch, 'success', 'Portafolio creado exitosamente');
      setOpen(false);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeType = (type: 'template' | 'custom') => {
    setType(type);
    setTemplate(undefined);
    setOperation({ enabled: false });
    setTitle('');
    setValidUntil(null);
    setDocuments([]);
    setSelectedValidations([]);
  };

  const handleChangeTemplate = (template: string) => {
    setTemplate(template);
    setOperation({ enabled: false });
    setTitle('');
    setValidUntil(null);
    setDocuments([]);
    setSelectedValidations([]);
    setBusinessUserIds([]);
    setSelectedOwnUserIds([]);
    setSelectedValidations([]);
  };

  const handleSave = async () => {
    if (type === 'custom') {
      await handleCreatePortfolio();
    } else {
      await handleCreatePortfolioFromTemplate();
    }
    void fetchPortfolios({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    void fetchTemplates();
    void fetchSigners();
    void fetchPortoflioValidations();
  }, []);

  useEffect(() => {
    if (template && templates.length > 0) {
      const selectedTemplateData = templates.find((t) => t.id === template);
      if (selectedTemplateData?.title) {
        setTitle(selectedTemplateData.title);
      }
    }
  }, [template, templates]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Title
          title="Crear portafolio"
          icon={<Folder color="primary" />}
          color="primary"
        />
        <FormControl margin="dense" size="small" style={{ width: '200px' }}>
          <InputLabel id="type-label">Tipo de portafolio</InputLabel>
          <Select
            labelId="type-label"
            id="type-select"
            size="small"
            value={type}
            label="Tipo de portafolio"
            onChange={(e) =>
              handleChangeType(e.target.value as 'template' | 'custom')
            }
            MenuProps={{
              MenuListProps: { dense: true },
            }}
          >
            {['custom', 'template'].map((type: string, index: number) => (
              <MenuItem key={index} value={type} dense>
                {type === 'custom' ? 'Personalizado' : 'Plantilla'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogTitle>
      <DialogContent>
        {type === 'custom' && (
          <Custom
            businessIdentifier={businessIdentifier}
            validations={validations}
            ownUsers={ownUsers}
            businessUsers={businessUsers}
            selectedOwnUserIds={selectedOwnUserIds}
            setSelectedOwnUserIds={setSelectedOwnUserIds}
            businessUserIds={businessUserIds}
            setBusinessUserIds={setBusinessUserIds}
            selectedValidations={selectedValidations}
            setSelectedValidations={setSelectedValidations}
            title={title}
            setTitle={setTitle}
            validUntil={validUntil}
            setValidUntil={setValidUntil}
            operation={operation}
            setOperation={setOperation}
            documents={documents}
            setDocuments={setDocuments}
          />
        )}
        {type === 'template' && (
          <Template
            templates={templates}
            selectedTemplate={template}
            title={title}
            setTitle={setTitle}
            template={template}
            setTemplate={handleChangeTemplate}
            operation={operation}
            setOperation={setOperation}
            businessIdentifier={businessIdentifier}
            businessUsers={businessUsers}
            businessUserIds={businessUserIds}
            setBusinessUserIds={setBusinessUserIds}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={() => handleSave()}
          loading={loading}
        >
          Crear portafolio
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePortfolio;
